import { Link } from "react-router-dom";
import { useEffect, useRef, useState } from "react";
import { useI18n } from "../../../i18n/I18nContext";
import Footer from "../Footer/Footer";

function useRevealOnScroll(count) {
  const refs = useRef([]);
  const [visible, setVisible] = useState(new Array(count).fill(false));

  useEffect(() => {
    const observers = refs.current.map((el, i) => {
      if (!el) return null;
      const observer = new IntersectionObserver(
        ([entry]) => {
          if (entry.isIntersecting) {
            setVisible((prev) => {
              const next = [...prev];
              next[i] = true;
              return next;
            });
            observer.disconnect();
          }
        },
        { threshold: 0.15 }
      );
      observer.observe(el);
      return observer;
    });
    return () => observers.forEach((o) => o?.disconnect());
  }, []);

  return { refs, visible };
}

export default function About() {
  const { t } = useI18n();

  const valores = [
    {
      emoji: "🏆",
      title: t("Qualidade em Primeiro Lugar"),
      text: t("Utilizamos apenas materiais premium e testes rigorosos para garantir que as nossas estantes cumprem os mais altos padrões."),
    },
    {
      emoji: "💡",
      title: t("Inovação"),
      text: t("Desenvolvemos continuamente novas soluções para enfrentar os desafios de armazenamento em armazéns modernos."),
    },
    {
      emoji: "🤝",
      title: t("Foco no Cliente"),
      text: t("O seu sucesso é a nossa prioridade. Fornecemos suporte contínuo e manutenção para todos os nossos sistemas."),
    },
    {
      emoji: "🌱",
      title: t("Sustentabilidade"),
      text: t("Processos de fabrico ecológicos e materiais recicláveis para um futuro mais verde."),
    },
  ];

  const conquistas = [
    t("Mais de 500 projetos realizados com sucesso"),
    t("Cobertura nacional em Portugal"),
    t("Certificações de qualidade ISO 9001"),
    t("Equipa técnica especializada com 20+ anos de experiência"),
    t("Parcerias estratégicas com líderes do setor"),
    t("Soluções customizadas para diversos segmentos"),
  ];

  const stats = [
    { icon: "🎖️", value: "20+", label: t("Anos de Experiência") },
    { icon: "⏱️", value: "48 Horas", label: t("Tempos de Entrega") },
    { icon: "🌍", value: "EU", label: t("Cobertura Alargada") },
  ];

  const pares = Math.ceil(conquistas.length / 2);
  const { refs, visible } = useRevealOnScroll(pares);

  return (
    <div className="min-h-screen bg-white flex flex-col">

      <section className="bg-indigo-700 pt-16">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-10 py-16 lg:py-24 text-center">
          <span className="inline-block text-xs font-semibold uppercase tracking-widest text-indigo-200 mb-4 bg-white/10 px-3 py-1 rounded-full">
            {t("A nossa empresa")}
          </span>
          <h1 className="text-3xl sm:text-4xl lg:text-5xl font-bold text-white mb-5 leading-tight">
            {t("Sobre a")} <span className="text-indigo-200">racksforall</span>
          </h1>
          <p className="text-indigo-100/80 max-w-2xl mx-auto text-base sm:text-lg">
            {t("Fornecedor europeu de soluções de armazenamento de qualidade para empresas. Sistemas de estantes profissionais para armazéns, projetados para fiabilidade e eficiência.")}
          </p>
        </div>

        <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-10 pb-16">
          <div className="grid grid-cols-3 gap-4">
            {stats.map((s) => (
              <div key={s.value} className="rounded-2xl bg-white/10 border border-white/20 px-4 py-5 text-center">
                <div className="text-2xl mb-2">{s.icon}</div>
                <p className="text-xl font-bold text-white">{s.value}</p>
                <p className="text-xs text-indigo-200/75 mt-1">{s.label}</p>
              </div>
            ))}
          </div>
        </div>

        <div className="overflow-hidden leading-none">
          <svg viewBox="0 0 1440 60" className="w-full" preserveAspectRatio="none" style={{ height: 60 }}>
            <path d="M0,40 C360,80 1080,0 1440,40 L1440,60 L0,60 Z" fill="white" />
          </svg>
        </div>
      </section>

      <section className="py-20 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-10">
          <div className="grid lg:grid-cols-2 gap-10 items-start">

            {/* Texto principal — design moderno */}
            <div className="rounded-3xl border border-gray-200 bg-white shadow-md overflow-hidden">
              <div className="bg-indigo-600 px-8 py-6">
                <span className="inline-block text-xs font-semibold uppercase tracking-widest text-indigo-200 mb-2">
                  {t("Quem somos")}
                </span>
                <h2 className="text-xl sm:text-2xl font-bold text-white">
                  {t("Sistemas de Armazenamento Profissionais")}
                </h2>
              </div>
              <div className="px-8 py-7 space-y-5">
                <div className="flex gap-4 items-start">
                  <div className="mt-1 flex h-8 w-8 shrink-0 items-center justify-center rounded-xl bg-indigo-50 text-indigo-600">
                    <svg className="w-4 h-4" fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" d="M3.055 11H5a2 2 0 012 2v1a2 2 0 002 2 2 2 0 012 2v2.945" />
                      <path strokeLinecap="round" strokeLinejoin="round" d="M8 3.935V5.5A2.5 2.5 0 0010.5 8h.5a2 2 0 012 2 2 2 0 104 0 2 2 0 012-2h1.064" />
                      <circle cx="12" cy="12" r="9" />
                    </svg>
                  </div>
                  <div>
                    <p className="text-sm font-semibold text-gray-900 mb-1">{t("Presença Europeia")}</p>
                    <p className="text-sm text-gray-500 leading-relaxed">
                      {t("Especializamo-nos em fornecer sistemas de estantes de alta qualidade para armazéns e instalações industriais em toda a Europa, com foco em fiabilidade e durabilidade.")}
                    </p>
                  </div>
                </div>
                <div className="h-px bg-gray-100" />
                <div className="flex gap-4 items-start">
                  <div className="mt-1 flex h-8 w-8 shrink-0 items-center justify-center rounded-xl bg-indigo-50 text-indigo-600">
                    <svg className="w-4 h-4" fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" d="M20 7l-8-4-8 4m16 0l-8 4m8-4v10l-8 4m0-10L4 7m8 4v10" />
                    </svg>
                  </div>
                  <div>
                    <p className="text-sm font-semibold text-gray-900 mb-1">{t("Gama Completa de Produtos")}</p>
                    <p className="text-sm text-gray-500 leading-relaxed">
                      {t("Estantes porta-paletes, sistemas de prateleiras e soluções personalizadas desenhadas para maximizar a eficiência do espaço com total segurança.")}
                    </p>
                  </div>
                </div>
                <div className="h-px bg-gray-100" />
                <div className="flex gap-4 items-start">
                  <div className="mt-1 flex h-8 w-8 shrink-0 items-center justify-center rounded-xl bg-indigo-50 text-indigo-600">
                    <svg className="w-4 h-4" fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" d="M17 20h5v-2a3 3 0 00-5.356-1.857M17 20H7m10 0v-2c0-.656-.126-1.283-.356-1.857M7 20H2v-2a3 3 0 015.356-1.857M7 20v-2c0-.656.126-1.283.356-1.857m0 0a5.002 5.002 0 019.288 0" />
                    </svg>
                  </div>
                  <div>
                    <p className="text-sm font-semibold text-gray-900 mb-1">{t("Para Todos os Tamanhos")}</p>
                    <p className="text-sm text-gray-500 leading-relaxed">
                      {t("Desde pequenos armazéns a grandes centros de distribuição, oferecemos consultoria profissional e suporte completo durante todo o processo.")}
                    </p>
                  </div>
                </div>
              </div>
            </div>

            <div className="space-y-4">
              {valores.map((v) => (
                <div key={v.title} className="flex gap-4 rounded-2xl border border-gray-200 bg-white p-5 shadow-md hover:shadow-lg hover:-translate-y-0.5 transition-all">
                  <div className="text-2xl shrink-0 mt-0.5">{v.emoji}</div>
                  <div>
                    <h3 className="font-semibold text-gray-900 mb-1">{v.title}</h3>
                    <p className="text-sm text-gray-500 leading-relaxed">{v.text}</p>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      <section className="py-20 bg-gray-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-10">
          <div className="text-center mb-12">
            <h2 className="text-2xl sm:text-3xl font-bold text-gray-900 mb-3">{t("As Nossas Conquistas")}</h2>
            <p className="text-gray-500 text-sm sm:text-base">{t("O resultado de anos de dedicação e compromisso com a excelência")}</p>
          </div>

          <div className="max-w-4xl mx-auto space-y-4">
            {Array.from({ length: pares }).map((_, pairIndex) => (
              <div
                key={pairIndex}
                ref={(el) => (refs.current[pairIndex] = el)}
                className={`grid sm:grid-cols-2 gap-4 transition-all duration-700 ease-out ${
                  visible[pairIndex]
                    ? "opacity-100 translate-y-0"
                    : "opacity-0 translate-y-8"
                }`}
                style={{ transitionDelay: `${pairIndex * 80}ms` }}
              >
                {conquistas.slice(pairIndex * 2, pairIndex * 2 + 2).map((c) => (
                  <div key={c} className="flex items-center gap-3 rounded-2xl border border-gray-200 bg-white px-5 py-4 shadow-md">
                    <div className="flex h-6 w-6 shrink-0 items-center justify-center rounded-full border-2 border-indigo-500 text-indigo-600">
                      <svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" strokeWidth="2.5" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" d="M5 13l4 4L19 7" />
                      </svg>
                    </div>
                    <span className="text-sm text-gray-700 font-medium">{c}</span>
                  </div>
                ))}
              </div>
            ))}
          </div>
        </div>
      </section>

      <section className="py-20 bg-white">
        <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-10 text-center">
          <div className="rounded-3xl border border-gray-200 bg-gray-50 shadow-md px-8 py-12">
            <h2 className="text-2xl sm:text-3xl font-bold text-gray-900 mb-3">{t("Vamos Trabalhar Juntos?")}</h2>
            <p className="text-gray-500 mb-8 text-sm sm:text-base max-w-lg mx-auto">
              {t("Descubra como podemos ajudar a otimizar o seu espaço de armazenamento com soluções personalizadas.")}
            </p>
            <div className="flex flex-wrap gap-3 justify-center">
              <Link to="/contact" className="px-8 py-3 rounded-xl bg-indigo-600 text-white font-semibold text-sm hover:bg-indigo-700 shadow-sm transition">
                {t("Contacte-nos")}
              </Link>
              <Link to="/products" className="px-8 py-3 rounded-xl border border-gray-200 text-sm font-semibold text-gray-700 hover:bg-gray-100 transition">
                {t("Ver Produtos")}
              </Link>
            </div>
          </div>
        </div>
      </section>

      <Footer />
    </div>
  );
}